import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { humanMessage } from '../../lib/errors';
import { isPlatformAuthenticatorAvailable } from './webauthn';
import { usePasskeys } from './usePasskeys';

/**
 * «Заходь пальцем» — те саме, що й passkey в кабінеті батьків, але дитячою
 * мовою і без жодної адміністрації.
 *
 * Три рішення, які роблять цей екран дитячим:
 *
 * 1. Картка є ТІЛЬКИ там, де в пристрої справді є відбиток або Face ID.
 *    Решта доріг WebAuthn (QR-код на телефон поруч, апаратний ключ) для
 *    дев'ятирічного — глухий кут, і пропонувати їх означає підставити його.
 *
 * 2. Дат і транспортів тут немає. Дитині досить двох станів: увімкнено або ні.
 *    Повний список ключів із «останній вхід …» лишається батькам.
 *
 * 3. Кнопка «Увімкнути» не ховається після першого ключа: у дитини може бути
 *    і телефон, і планшет. Повторне натискання на ВЖЕ доданому пристрої
 *    браузер відхилить сам, і про це чесно скаже підпис під кнопкою.
 */
export function QuickLoginCard(): React.JSX.Element | null {
  const [platformReady, setPlatformReady] = useState<boolean | null>(null);
  // Поки не знаємо, чи є в пристрої відбиток, — не питаємо сервер про ключі:
  // на пів родини пристроїв ця відповідь узагалі нікому не знадобиться.
  const passkeys = usePasskeys({ enabled: platformReady === true });

  useEffect(() => {
    void isPlatformAuthenticatorAvailable().then(setPlatformReady);
  }, []);

  const { add, removeAll } = passkeys;

  if (platformReady !== true) return null;
  // Список не доїхав (найчастіше — інтернету немає). Мовчимо: ключ усе одно
  // не додати, а дитині на арені зайва червона помилка ні до чого.
  if (passkeys.isError) return null;

  const enabled = passkeys.list.length > 0;
  const devices = passkeys.list.length;

  return (
    <Panel className="p-4" data-testid="quick-login">
      <div className="flex items-center gap-3">
        <Icon name="fingerprint" size={24} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black">Заходь пальцем</p>
          <p className="text-xs font-semibold text-white/55">
            {enabled
              ? devices > 1
                ? `Увімкнено на ${devices} пристроях — PIN більше не потрібен.`
                : 'Увімкнено — PIN більше не потрібен.'
              : 'Замість PIN — відбиток або Face ID. Ключ лишається в телефоні.'}
          </p>
        </div>
        {enabled ? (
          <span className="lk-chip shrink-0 text-[var(--lk-green)]">
            <Icon name="correct" size={14} />
            увімкнено
          </span>
        ) : null}
      </div>

      {/*
        Поки вимкнено — велика запрошувальна кнопка. Коли вже увімкнено, обидві
        дії стають дрібними: справу зроблено, і галасувати про неї на екрані,
        де головне — «Грати», більше нема чого.
      */}
      {enabled ? (
        <div className="mt-3 flex items-center justify-center gap-3 text-xs font-bold text-white/45">
          <button
            type="button"
            className="cursor-pointer"
            disabled={add.isPending || removeAll.isPending}
            onClick={() => add.mutate()}
            data-testid="quick-login-enable"
          >
            {add.isPending ? 'додаємо…' : 'додати ще пристрій'}
          </button>
          <span aria-hidden="true" className="text-white/20">
            ·
          </span>
          <button
            type="button"
            className="cursor-pointer"
            disabled={removeAll.isPending || add.isPending}
            onClick={() => removeAll.mutate()}
          >
            {devices > 1 ? 'вимкнути скрізь' : 'вимкнути'}
          </button>
        </div>
      ) : (
        <Button
          tone="ghost"
          block
          className="mt-3"
          loading={add.isPending}
          disabled={add.isPending}
          onClick={() => add.mutate()}
          data-testid="quick-login-enable"
        >
          <Icon name="fingerprint" size={20} />
          Увімкнути на цьому пристрої
        </Button>
      )}

      {add.isError ? (
        <p className="mt-2 text-center text-xs font-bold text-[var(--lk-red)]" role="alert">
          {humanMessage(add.error)}
        </p>
      ) : null}
      {add.isSuccess ? (
        <p className="mt-2 text-center text-xs font-bold text-[var(--lk-green)]" role="status">
          Готово! Наступного разу тисни «Face ID / відбиток» замість PIN.
        </p>
      ) : null}
      {removeAll.isError ? (
        <p className="mt-2 text-center text-xs font-bold text-[var(--lk-red)]" role="alert">
          {humanMessage(removeAll.error)}
        </p>
      ) : null}
    </Panel>
  );
}
